// =====================================================================================
// FILE: src/services/operatorAirtable.js
// 
// Airtable service for Operator Portal
// Handles fetching and updating records across multiple club bases
// Supports: CG Operator View, Ribbon Operator View, TVC, IPTV, LED tables
// =====================================================================================

import {
  OPERATOR_BASES,
  OPERATOR_FIELDS,
  OPERATOR_CONTENT_TYPES,
  getOperatorBase,
  getOperatorTableId,
  getOperatorBaseId,
  isSharedTable,
} from '../config/operatorBases';

const TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;

// ================= CORE HELPERS =================

function assertToken() {
  if (!TOKEN) {
    throw new Error('Missing env var: REACT_APP_AIRTABLE_TOKEN');
  }
}

function tableUrl(baseId, tableId, path = '') {
  return `https://api.airtable.com/v0/${baseId}/${tableId}${path}`;
}

async function airtableFetch(url, options = {}) {
  assertToken();

  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${TOKEN}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Airtable response not JSON: ${text.slice(0, 200)}`);
  }

  if (!res.ok) {
    const errMsg = data?.error?.message || JSON.stringify(data);
    throw new Error(`Airtable error (${res.status}): ${errMsg}`);
  }

  return data;
}

// ================= VIEW FETCHING =================

/**
 * Fetch all views for a table (to get fixture/match views)
 */
async function fetchTableViews(baseId, tableId) {
  if (!tableId) return [];
  
  try {
    const url = `https://api.airtable.com/v0/meta/bases/${baseId}/tables`;
    console.log(`[OperatorAirtable] Fetching table metadata for base ${baseId}`);
    const data = await airtableFetch(url);
    
    const table = data.tables?.find(t => t.id === tableId);
    if (!table?.views) {
      console.warn(`[OperatorAirtable] Table ${tableId} not found or has no views`);
      return [];
    }
    
    console.log(`[OperatorAirtable] Table ${table.name} has ${table.views.length} views`);
    
    return table.views.map(v => ({
      id: v.id,
      name: v.name,
      type: v.type,
    }));
  } catch (err) {
    console.error(`[OperatorAirtable] Failed to fetch views for ${tableId}:`, err);
    return [];
  }
}

/**
 * Fetch all category options from a club's operator table field metadata
 * Uses the Airtable metadata API to get all Single Select options for the Category field
 * This returns ALL defined options, not just ones currently in use on records
 */
export async function fetchOperatorCategories(clubId, contentType) {
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) return [];
  
  const baseId = getOperatorBaseId(clubId, contentType);
  if (!baseId) return [];
  
  try {
    // Use metadata API to get field options
    const url = `https://api.airtable.com/v0/meta/bases/${baseId}/tables`;
    const data = await airtableFetch(url);
    
    const table = data.tables?.find(t => t.id === tableId);
    if (!table?.fields) {
      console.warn('[OperatorAirtable] No fields found in table metadata, falling back to record scan');
      return fetchOperatorCategoriesFromRecords(baseId, tableId);
    }
    
    // Find the Category field
    const categoryField = table.fields.find(f => f.name === 'Category');
    if (!categoryField?.options?.choices) {
      console.warn('[OperatorAirtable] Category field has no choices, falling back to record scan');
      return fetchOperatorCategoriesFromRecords(baseId, tableId);
    }
    
    // Extract option names in Airtable's configured order (not alphabetical)
    // This matches the order defined in the Category single select field
    const categories = categoryField.options.choices.map(choice => choice.name);
    
    console.log(`[OperatorAirtable] Loaded ${categories.length} category options from field metadata (Airtable order)`);
    return categories;
  } catch (err) {
    console.warn('[OperatorAirtable] Metadata fetch failed, falling back to record scan:', err);
    return fetchOperatorCategoriesFromRecords(baseId, tableId);
  }
}

/**
 * Fallback: Fetch categories by scanning existing records
 */
async function fetchOperatorCategoriesFromRecords(baseId, tableId) {
  const categories = new Set();
  let offset = null;
  
  do {
    const params = new URLSearchParams();
    params.set('fields[]', 'Category');
    if (offset) params.set('offset', offset);
    
    const url = `${tableUrl(baseId, tableId)}?${params.toString()}`;
    const data = await airtableFetch(url);
    
    if (data.records) {
      data.records.forEach(rec => {
        const cat = rec.fields?.Category;
        if (cat) categories.add(cat);
      });
    }
    
    offset = data.offset || null;
  } while (offset);
  
  return [...categories];
}

/**
 * Fetch views for a club's operator tables
 */
export async function fetchOperatorViews(clubId, contentType) {
  const base = getOperatorBase(clubId);
  if (!base) {
    console.warn(`[OperatorAirtable] No base found for club: ${clubId}`);
    return [];
  }
  
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    console.warn(`[OperatorAirtable] No ${contentType} table for club: ${clubId}`);
    return [];
  }
  
  return fetchTableViews(base.baseId, tableId);
}

/**
 * Fetch views for ALL clubs (aggregated)
 */
export async function fetchAllOperatorViews(contentType) {
  const allViews = [];
  
  console.log(`[OperatorAirtable] Fetching ${contentType} views for ${OPERATOR_BASES.length} clubs:`, 
    OPERATOR_BASES.map(b => b.id));
  
  for (const base of OPERATOR_BASES) {
    const tableId = contentType === OPERATOR_CONTENT_TYPES.CG 
      ? base.cgTableId 
      : base.ribbonTableId;
    
    console.log(`[OperatorAirtable] ${base.id} - ${contentType} tableId:`, tableId || '(not configured)');
      
    if (!tableId) continue;
    
    const views = await fetchTableViews(base.baseId, tableId);
    console.log(`[OperatorAirtable] ${base.id} - Found ${views.length} views:`, views.map(v => v.name));
    
    // Add club context to each view
    views.forEach(v => {
      allViews.push({
        ...v,
        clubId: base.id,
        clubName: base.name,
        clubShortName: base.shortName,
      });
    });
  }
  
  console.log(`[OperatorAirtable] Total views across all clubs: ${allViews.length}`);
  return allViews;
}

// ================= RECORD FETCHING =================

// Field names for content in each table type
const CONTENT_FIELD_NAMES = {
  cg: ['CG Content'],
  ribbon: ['Ribbon Content'],
  tvc: ['Big Screen - TVC', 'Corner Screen - TVC'],
  iptv: ['IPTV GFX'],
  led: ['LED - All Types'],
};

/**
 * Fetch asset details by IDs from the Assets table
 */
async function fetchAssetsByIds(baseId, assetIds) {
  if (!assetIds || assetIds.length === 0) return {};
  
  // Assets table ID (same as Match Builder uses)
  const assetsTableId = process.env.REACT_APP_AIRTABLE_ASSETS_TABLE_ID;
  if (!assetsTableId) {
    console.warn('[OperatorAirtable] No REACT_APP_AIRTABLE_ASSETS_TABLE_ID configured');
    return {};
  }
  
  const assetMap = {};
  
  // Airtable formula to filter by record IDs
  const uniqueIds = [...new Set(assetIds.filter(Boolean))];
  if (uniqueIds.length === 0) return {};
  
  console.log('[OperatorAirtable] Fetching assets for IDs:', uniqueIds.slice(0, 5), '...');
  
  // Fetch in batches of 50 to avoid URL length limits
  const batchSize = 50;
  for (let i = 0; i < uniqueIds.length; i += batchSize) {
    const batch = uniqueIds.slice(i, i + batchSize);
    const formula = `OR(${batch.map(id => `RECORD_ID()='${id}'`).join(',')})`;
    
    const params = new URLSearchParams();
    params.set('filterByFormula', formula);
    // Don't use cellFormat=string so we get attachment objects with thumbnails
    
    try {
      const url = `${tableUrl(baseId, assetsTableId)}?${params.toString()}`;
      const data = await airtableFetch(url);
      
      if (data.records) {
        data.records.forEach(asset => {
          const fields = asset.fields || {};
          
          // DEBUG: Log first asset's field names to find ingest fields
          if (!window._assetFieldsLogged) {
            console.log('[OperatorAirtable] Asset fields available:', Object.keys(fields));
            console.log('[OperatorAirtable] Looking for ingest fields...');
            Object.keys(fields).forEach(key => {
              if (key.toLowerCase().includes('ingest') || key.toLowerCase().includes('mcg') || key.toLowerCase().includes('marvel')) {
                console.log(`[OperatorAirtable] Found field: "${key}" =`, fields[key]);
              }
              // Also log Last Modified fields
              if (key.toLowerCase().includes('modified') || key.toLowerCase().includes('last')) {
                console.log(`[OperatorAirtable] Found time field: "${key}" =`, fields[key]);
              }
            });
            window._assetFieldsLogged = true;
          }
          
          // Get thumbnail from Asset Thumbnails attachment field
          const thumbnails = fields['Asset Thumbnails'];
          const thumbnailUrl = Array.isArray(thumbnails) && thumbnails[0]?.thumbnails?.large?.url
            ? thumbnails[0].thumbnails.large.url
            : (Array.isArray(thumbnails) && thumbnails[0]?.thumbnails?.small?.url
              ? thumbnails[0].thumbnails.small.url
              : null);
          
          // Get preview URL for video playback
          // Asset Preview URL may be a rich text field with multiple URLs
          let previewUrl = '';
          const rawPreviewUrl = fields['Asset Preview URL'] || fields['Dropbox URL'] || '';
          if (typeof rawPreviewUrl === 'string') {
            // Extract first URL from comma/newline separated list
            previewUrl = rawPreviewUrl.split(/[,\n]/)[0].trim();
            // If it's a markdown link like [text](url), extract just the URL
            const mdMatch = previewUrl.match(/\[.*?\]\((.*?)\)/);
            if (mdMatch) {
              previewUrl = mdMatch[1];
            }
          }
          
          // DEBUG: Log preview URL parsing for first asset
          if (!window._previewUrlLogged && rawPreviewUrl) {
            console.log('[OperatorAirtable] Raw Asset Preview URL:', rawPreviewUrl);
            console.log('[OperatorAirtable] Parsed Preview URL:', previewUrl);
            window._previewUrlLogged = true;
          }
          
          // Get ingest status fields (for CG/Ribbon watch folder indicator)
          // Try multiple possible field name variations
          const marvelIngest = fields['Marvel Ingested'] || fields['Marvel Ingest'] || fields['Marvel ingest'] || fields['marvel ingested'] || false;
          const mcgIngest = fields['MCG Ingested'] || fields['MCG Ingest'] || fields['MCG ingest'] || fields['mcg ingested'] || false;
          
          // Get download URL for manual watch folder download
          // Asset Download URL may be a rich text/markdown field
          let downloadUrl = '';
          const rawDownloadUrl = fields['Asset Download URL'] || '';
          if (typeof rawDownloadUrl === 'string' && rawDownloadUrl.trim()) {
            // Extract first URL from comma/newline separated list
            downloadUrl = rawDownloadUrl.split(/[,\n]/)[0].trim();
            // If it's a markdown link like [text](url), extract just the URL
            const dlMdMatch = downloadUrl.match(/\[.*?\]\((.*?)\)/);
            if (dlMdMatch) {
              downloadUrl = dlMdMatch[1];
            }
          }
          
          assetMap[asset.id] = {
            id: asset.id,
            name: fields['Name'] || fields['Asset Name'] || '',
            lumen8Id: fields['Lumen8 ID'] || '',
            assetDestination: fields['Asset Destination'] || '',
            hasAudio: fields['Has Audio'],
            hasAlpha: fields['Has Alpha'],
            duration: fields['Duration'] || '',
            dropboxUrl: previewUrl || fields['Dropbox URL'] || '', // ✅ Use previewUrl (from Asset Preview URL) as primary source
            previewUrl: previewUrl,
            downloadUrl: downloadUrl, // ✅ Direct download URL (parsed from Markdown)
            thumbnailUrl: thumbnailUrl,
            marvelIngest: marvelIngest,
            mcgIngest: mcgIngest,
            archived: fields['Archived'] || fields['Is Archived'] || fields['Hidden'] || fields['Is Hidden'] || false,
            lastModified: fields['Last Modified Time'] || fields['Last Modified'] || '', // ✅ Track when content was last edited
            groupedAssetLinksRaw: fields['Grouped Asset Links'] || '', // JSON string with level file URLs
          };
        });
      }
    } catch (err) {
      console.error('[OperatorAirtable] Failed to fetch assets batch:', err);
    }
  }
  
  console.log('[OperatorAirtable] Asset map built with', Object.keys(assetMap).length, 'assets');
  
  // ── Parse Grouped Asset Links for tiled and 360 LED preview ──
  // The field is a JSON string in the club's Assets table containing
  // [{ "url": "__https://...&dl=0", "name": "..._MCG-TL-FOP.mp4", "downloadUrl": "https://...&dl=1__" }, ...]
  const stripWraps = (s) => (s || "").replace(/^_+/, "").replace(/_+$/, "").trim();
  
  Object.values(assetMap).forEach(a => {
    const raw = a.groupedAssetLinksRaw;
    if (!raw || typeof raw !== 'string' || !raw.trim()) return;
    
    const dest = (a.assetDestination || '').toLowerCase();
    if (!dest.includes('tiled') && !dest.includes('360')) return;
    
    try {
      if (raw.trim().startsWith("[")) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          a.ribbonItems = parsed
            .filter(item => item && (item.downloadUrl || item.url))
            .map(item => ({
              name: item.name || "",
              downloadUrl: stripWraps(item.downloadUrl) || stripWraps(item.url),
            }))
            .filter(item => item.downloadUrl);
        }
      }
    } catch (err) {
      // Not valid JSON — try markdown/URL fallback
      const items = raw.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
        .map(entry => {
          const mdMatch = entry.match(/\[(.*?)\]\((.*?)\)/);
          if (mdMatch) return { name: mdMatch[1], downloadUrl: stripWraps(mdMatch[2]) };
          return null;
        })
        .filter(Boolean);
      if (items.length > 0) a.ribbonItems = items;
    }
    
    if (a.ribbonItems?.length > 0) {
      console.log(`[OperatorAirtable] Parsed ${a.ribbonItems.length} level files for ${a.lumen8Id} (${dest})`);
    }
  });
  
  return assetMap;
}

/**
 * Extract asset IDs from records based on content type
 */
function extractAssetIds(records, contentType) {
  const fieldNames = CONTENT_FIELD_NAMES[contentType] || [];
  const assetIds = [];
  
  records.forEach(rec => {
    fieldNames.forEach(fieldName => {
      const value = rec.fields?.[fieldName];
      if (Array.isArray(value)) {
        assetIds.push(...value);
      } else if (typeof value === 'string' && value.startsWith('rec')) {
        assetIds.push(value);
      }
    });
  });
  
  return assetIds;
}

/**
 * Enrich records with asset details
 */
function enrichRecordsWithAssets(records, assetMap, contentType) {
  const fieldNames = CONTENT_FIELD_NAMES[contentType] || [];
  
  return records.map(rec => {
    // Find the first non-empty content field
    let assetId = null;
    let contentFieldName = null;
    
    for (const fieldName of fieldNames) {
      const value = rec.fields?.[fieldName];
      if (Array.isArray(value) && value.length > 0) {
        assetId = value[0];
        contentFieldName = fieldName;
        break;
      } else if (typeof value === 'string' && value.startsWith('rec')) {
        assetId = value;
        contentFieldName = fieldName;
        break;
      }
    }
    
    const asset = assetId ? assetMap[assetId] : null;
    const assetMissing = assetId && !asset; // Asset ID exists but record not found = deleted/archived
    
    // Add enriched fields
    return {
      ...rec,
      fields: {
        ...rec.fields,
        // Add lookup-style fields for display
        'Lumen8 ID': asset?.lumen8Id || rec.fields?.['Lumen8 ID'] || '',
        'Asset Destination': asset?.assetDestination || rec.fields?.['Asset Destination'] || '',
        'Has Audio': asset?.hasAudio ?? rec.fields?.['Has Audio'],
        'Has Alpha': asset?.hasAlpha ?? rec.fields?.['Has Alpha'],
        'Duration': asset?.duration || rec.fields?.['Duration'] || '',
        'Dropbox URL': asset?.dropboxUrl || rec.fields?.['Dropbox URL'] || '',
        '_thumbnailUrl': asset?.thumbnailUrl || '',
        '_previewUrl': asset?.previewUrl || '',
        '_downloadUrl': asset?.downloadUrl || '',
        '_assetName': asset?.name || '',
        '_contentFieldName': contentFieldName,
        '_assetMissing': assetMissing,
        '_isArchived': !!(asset?.archived),
        '_assetId': assetId || '',
        '_marvelIngest': asset?.marvelIngest || false,
        '_mcgIngest': asset?.mcgIngest || false,
        '_lastModified': asset?.lastModified || rec.fields?.['Last Modified Time'] || rec.fields?.['Last Modified'] || '',
        '_ribbonItems': asset?.ribbonItems || [], // Tiled level files for 3D preview
      },
    };
  });
}

/**
 * Fetch records from an operator table
 * @param {string} clubId - Club identifier (used for CG/Ribbon, ignored for shared tables)
 * @param {string} contentType - 'cg', 'ribbon', 'tvc', 'iptv', or 'led'
 * @param {string} viewName - Optional Airtable view name (e.g., fixture view)
 */
export async function fetchOperatorRecords(clubId, contentType, viewName = null) {
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    throw new Error(`No ${contentType} table configured for club: ${clubId}`);
  }
  
  // Get the correct base ID (club-specific for CG/Ribbon, shared for TVC/IPTV/LED)
  const baseId = getOperatorBaseId(clubId, contentType);
  if (!baseId) {
    throw new Error(`No base configured for content type: ${contentType}`);
  }
  
  const base = getOperatorBase(clubId);
  const isShared = isSharedTable(contentType);
  
  const records = [];
  let offset = null;
  
  do {
    const params = new URLSearchParams();
    if (viewName) params.set('view', viewName);
    if (offset) params.set('offset', offset);
    
    // DON'T use cellFormat=string so we get record IDs for asset lookups
    // This allows us to enrich all content types with proper asset details
    
    const url = `${tableUrl(baseId, tableId)}?${params.toString()}`;
    const data = await airtableFetch(url);
    
    if (data.records) {
      // Add context to each record
      data.records.forEach(rec => {
        records.push({
          ...rec,
          _clubId: isShared ? 'SHARED' : clubId,
          _clubName: isShared ? 'All Clubs' : (base?.name || clubId),
          _contentType: contentType,
          _baseId: baseId,
          _tableId: tableId,
        });
      });
    }
    
    offset = data.offset || null;
  } while (offset);
  
  // Enrich ALL content types with asset details (CG, Ribbon, TVC, IPTV, LED)
  if (records.length > 0) {
    const assetIds = extractAssetIds(records, contentType);
    console.log(`[OperatorAirtable] ${contentType} - Extracted asset IDs:`, assetIds.length);
    if (assetIds.length > 0) {
      const assetMap = await fetchAssetsByIds(baseId, assetIds);
      console.log(`[OperatorAirtable] ${contentType} - Fetched assets:`, Object.keys(assetMap).length);
      return enrichRecordsWithAssets(records, assetMap, contentType);
    }
  }
  
  return records;
}

/**
 * Fetch records from ALL clubs for a content type
 * @param {string} contentType - 'cg', 'ribbon', 'tvc', 'iptv', or 'led'
 * @param {string} viewName - Optional view name (applied to all bases)
 * @param {string[]} clubFilter - Optional array of club IDs to filter (ignored for shared tables)
 */
export async function fetchAllOperatorRecords(contentType, viewName = null, clubFilter = null) {
  const allRecords = [];
  
  // For shared tables (TVC, IPTV, LED), only fetch once
  if (isSharedTable(contentType)) {
    try {
      const records = await fetchOperatorRecords(null, contentType, viewName);
      allRecords.push(...records);
    } catch (err) {
      console.error(`[OperatorAirtable] Failed to fetch ${contentType}:`, err);
    }
    return allRecords;
  }
  
  // For club-specific tables (CG, Ribbon), fetch from each club
  const basesToFetch = clubFilter 
    ? OPERATOR_BASES.filter(b => clubFilter.includes(b.id))
    : OPERATOR_BASES;
  
  for (const base of basesToFetch) {
    try {
      const records = await fetchOperatorRecords(base.id, contentType, viewName);
      allRecords.push(...records);
    } catch (err) {
      console.error(`[OperatorAirtable] Failed to fetch from ${base.name}:`, err);
      // Continue with other bases
    }
  }
  
  return allRecords;
}

// ================= RECORD UPDATES =================

/**
 * Update a single field on a record
 */
export async function updateOperatorField(clubId, contentType, recordId, fieldName, value) {
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    throw new Error(`No ${contentType} table configured`);
  }
  
  const baseId = getOperatorBaseId(clubId, contentType);
  if (!baseId) {
    throw new Error(`No base configured for content type: ${contentType}`);
  }
  
  const url = tableUrl(baseId, tableId);
  const data = await airtableFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({
      records: [{
        id: recordId,
        fields: { [fieldName]: value },
      }],
    }),
  });
  
  return data.records?.[0] || null;
}

/**
 * Update Content Status dropdown
 */
export async function updateContentStatus(clubId, contentType, recordId, status) {
  return updateOperatorField(clubId, contentType, recordId, OPERATOR_FIELDS.contentStatus, status);
}

/**
 * Update Lumen8 Comments
 */
export async function updateLumen8Comments(clubId, contentType, recordId, comments) {
  return updateOperatorField(clubId, contentType, recordId, OPERATOR_FIELDS.lumen8Comments, comments);
}

/**
 * Toggle "Send To Watch Folder" checkbox
 */
export async function toggleSendToWatchFolder(clubId, contentType, recordId, value) {
  return updateOperatorField(clubId, contentType, recordId, OPERATOR_FIELDS.sendToWatchFolder, value);
}

/**
 * Update Take ID field
 */
export async function updateTakeId(clubId, contentType, recordId, takeId) {
  return updateOperatorField(clubId, contentType, recordId, 'Take ID', takeId);
}

/**
 * Update Built at Venue checkbox + timestamp
 * When checked, stamps current date/time. When unchecked, clears timestamp.
 */
export async function updateBuiltAtVenue(clubId, contentType, recordId, checked) {
  const base = getOperatorBase(clubId);
  if (!base) throw new Error(`No base configured for club: ${clubId}`);
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) throw new Error(`No ${contentType} table configured for club: ${clubId}`);

  const fields = {
    [OPERATOR_FIELDS.builtAtVenue]: checked,
    [OPERATOR_FIELDS.builtAtVenueTime]: checked ? new Date().toISOString() : null,
  };

  const url = tableUrl(base.baseId, tableId);
  const data = await airtableFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({ records: [{ id: recordId, fields }] }),
  });
  return data.records?.[0] || null;
}

/**
 * Update multiple fields at once
 */
export async function updateOperatorRecord(clubId, contentType, recordId, fields) {
  const base = getOperatorBase(clubId);
  if (!base) {
    throw new Error(`No base configured for club: ${clubId}`);
  }
  
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    throw new Error(`No ${contentType} table configured for club: ${clubId}`);
  }
  
  const url = tableUrl(base.baseId, tableId);
  const data = await airtableFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({
      records: [{
        id: recordId,
        fields,
      }],
    }),
  });
  
  return data.records?.[0] || null;
}

/**
 * Batch update multiple records (e.g., bulk approve)
 */
export async function batchUpdateOperatorRecords(clubId, contentType, updates) {
  const base = getOperatorBase(clubId);
  if (!base) {
    throw new Error(`No base configured for club: ${clubId}`);
  }
  
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    throw new Error(`No ${contentType} table configured for club: ${clubId}`);
  }
  
  const url = tableUrl(base.baseId, tableId);
  
  // Airtable limits batch updates to 10 records
  const chunkSize = 10;
  const results = [];
  
  for (let i = 0; i < updates.length; i += chunkSize) {
    const chunk = updates.slice(i, i + chunkSize);
    const data = await airtableFetch(url, {
      method: 'PATCH',
      body: JSON.stringify({ records: chunk }),
    });
    
    if (data.records) {
      results.push(...data.records);
    }
  }
  
  return results;
}

// ================= FIXTURES =================

/**
 * Fetch fixtures for a club (for match selector dropdown)
 */
export async function fetchOperatorFixtures(clubId) {
  const base = getOperatorBase(clubId);
  
  // Try operator-specific fixtures table first
  if (base && base.fixturesTableId) {
    console.log(`[OperatorAirtable] Fetching fixtures from club table: ${clubId}`);
    const records = [];
    let offset = null;
    
    do {
      const params = new URLSearchParams();
      if (offset) params.set('offset', offset);
      params.set('cellFormat', 'string');
      params.set('timeZone', 'Australia/Melbourne');
      params.set('userLocale', 'en-AU');
      
      const url = `${tableUrl(base.baseId, base.fixturesTableId)}?${params.toString()}`;
      const data = await airtableFetch(url);
      
      if (data.records) {
        records.push(...data.records);
      }
      
      offset = data.offset || null;
    } while (offset);
    
    console.log(`[OperatorAirtable] Found ${records.length} fixtures`);
    return records;
  }
  
  // Fallback: Try main fixtures table
  const mainFixturesTableId = process.env.REACT_APP_AIRTABLE_FIXTURES_TABLE_ID;
  const mainBaseId = process.env.REACT_APP_AIRTABLE_BASE_ID;
  
  if (mainFixturesTableId && mainBaseId) {
    console.log(`[OperatorAirtable] Falling back to main fixtures table`);
    const records = [];
    let offset = null;
    
    do {
      const params = new URLSearchParams();
      if (offset) params.set('offset', offset);
      params.set('cellFormat', 'string');
      params.set('timeZone', 'Australia/Melbourne');
      params.set('userLocale', 'en-AU');
      
      const url = `${tableUrl(mainBaseId, mainFixturesTableId)}?${params.toString()}`;
      const data = await airtableFetch(url);
      
      if (data.records) {
        records.push(...data.records);
      }
      
      offset = data.offset || null;
    } while (offset);
    
    console.log(`[OperatorAirtable] Found ${records.length} fixtures from main table`);
    return records;
  }
  
  console.warn(`[OperatorAirtable] No fixtures table configured for club: ${clubId}`);
  return [];
}

// ================= ASSETS =================

/**
 * Fetch assets for a club (for asset picker)
 */
export async function fetchOperatorAssets(clubId, filterFormula = null) {
  const base = getOperatorBase(clubId);
  if (!base || !base.assetsTableId) {
    console.warn(`[OperatorAirtable] No assets table for club: ${clubId}`);
    return [];
  }
  
  const records = [];
  let offset = null;
  
  do {
    const params = new URLSearchParams();
    if (offset) params.set('offset', offset);
    if (filterFormula) params.set('filterByFormula', filterFormula);
    params.set('cellFormat', 'string');
    params.set('timeZone', 'Australia/Melbourne');
    params.set('userLocale', 'en-AU');
    
    const url = `${tableUrl(base.baseId, base.assetsTableId)}?${params.toString()}`;
    const data = await airtableFetch(url);
    
    if (data.records) {
      records.push(...data.records);
    }
    
    offset = data.offset || null;
  } while (offset);
  
  return records;
}

// ================= LINKED RECORD UPDATE =================

/**
 * Update linked asset field (CG Content or Ribbon Content)
 */
export async function updateLinkedAsset(clubId, contentType, recordId, assetRecordIds) {
  const fieldName = contentType === OPERATOR_CONTENT_TYPES.CG 
    ? OPERATOR_FIELDS.cgContent 
    : OPERATOR_FIELDS.ribbonContent;
    
  // Airtable linked records need to be an array of record IDs
  const value = Array.isArray(assetRecordIds) ? assetRecordIds : [assetRecordIds];
  
  return updateOperatorField(clubId, contentType, recordId, fieldName, value);
}

// ================= CREATE NEW ROW =================

/**
 * Create a new row in the operator table
 * Used by operators to add content on match day
 */
/**
 * Find an asset record by its Lumen8 ID string
 * @param {string} clubId - Club identifier
 * @param {string} lumenId - The Lumen8 ID to search for (e.g., "STK_X1W025S")
 * @returns {Promise<{recordId: string, name: string}|null>} Asset info or null if not found
 */
export async function findAssetByLumenId(clubId, lumenId) {
  if (!lumenId) return null;
  
  try {
    // Search assets with filter formula
    const formula = `{Lumen8 ID} = '${lumenId}'`;
    const assets = await fetchOperatorAssets(clubId, formula);
    
    if (assets && assets.length > 0) {
      const asset = assets[0];
      return {
        recordId: asset.id,
        name: asset.fields?.['Name'] || asset.fields?.['Asset Name'] || lumenId,
        lumenId: asset.fields?.['Lumen8 ID'] || lumenId,
      };
    }
    
    return null;
  } catch (err) {
    console.warn('[OperatorAirtable] Failed to find asset by Lumen8 ID:', err);
    return null;
  }
}

export async function createOperatorRow(clubId, contentType, fields) {
  const base = getOperatorBase(clubId);
  if (!base) {
    throw new Error(`No base configured for club: ${clubId}`);
  }
  
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    throw new Error(`No ${contentType} table configured for club: ${clubId}`);
  }
  
  const url = tableUrl(base.baseId, tableId);
  
  console.log('[operatorAirtable] Creating new row:', { clubId, contentType, fields });
  
  const data = await airtableFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      records: [{
        fields,
      }],
    }),
  });
  
  if (data.records && data.records.length > 0) {
    const newRecord = data.records[0];
    // Add metadata for consistency
    newRecord._clubId = clubId;
    newRecord._contentType = contentType;
    console.log('[operatorAirtable] Created new row:', newRecord.id);
    return newRecord;
  }
  
  throw new Error('Failed to create record - no record returned');
}

/**
 * Delete an operator row
 * @param {string} clubId - Club identifier
 * @param {string} contentType - 'cg', 'ribbon', etc.
 * @param {string} recordId - Airtable record ID to delete
 * @returns {Promise<boolean>} - True if deleted successfully
 */
export async function deleteOperatorRow(clubId, contentType, recordId) {
  const base = getOperatorBase(clubId);
  if (!base) {
    throw new Error(`No base configured for club: ${clubId}`);
  }
  
  const tableId = getOperatorTableId(clubId, contentType);
  if (!tableId) {
    throw new Error(`No ${contentType} table configured for club: ${clubId}`);
  }
  
  const url = `${tableUrl(base.baseId, tableId)}/${recordId}`;
  
  console.log('[operatorAirtable] Deleting row:', { clubId, contentType, recordId });
  
  const data = await airtableFetch(url, {
    method: 'DELETE',
  });
  
  if (data.deleted) {
    console.log('[operatorAirtable] Deleted row:', recordId);
    return true;
  }
  
  throw new Error('Failed to delete record');
}

/**
 * Fetch content pipeline statistics (counts by Content Status)
 * Returns counts for: New Submission, Rejected, Approved by Lumen8
 * 
 * @param {string} clubId - Club ID to fetch stats for (or null for current club from env)
 * @returns {Promise<{newSubmission: number, rejected: number, approved: number}>}
 */
export async function fetchContentPipelineStats(clubId = null) {
  const stats = {
    newSubmission: 0,  // Ready for Testing
    rejected: 0,       // Rejected
    approved: 0,       // Approved by Lumen8
  };
  
  // Use environment club if not specified
  const targetClubId = clubId || process.env.REACT_APP_DEFAULT_CLUB || 'STK';
  
  // Content types to check (CG and Ribbon are club-specific)
  const contentTypes = ['cg', 'ribbon'];
  
  try {
    for (const contentType of contentTypes) {
      try {
        const records = await fetchOperatorRecords(targetClubId, contentType);
        
        records.forEach(record => {
          const status = record.fields?.[OPERATOR_FIELDS.contentStatus];
          
          if (status === 'Ready for Testing') {
            stats.newSubmission++;
          } else if (status === 'Rejected') {
            stats.rejected++;
          } else if (status === 'Approved by Lumen8') {
            stats.approved++;
          }
        });
      } catch (err) {
        // Skip content types that fail (table might not exist)
        console.warn(`[Pipeline Stats] Failed to fetch ${contentType} for ${targetClubId}:`, err.message);
      }
    }
  } catch (err) {
    console.error('[Pipeline Stats] Error fetching stats:', err);
  }
  
  return stats;
}

// ================= FORCE RESEND — CLEAR INGEST FLAG =================

/**
 * Clear the MCG Ingested or Marvel Ingested flag on an asset record
 * in the content database so the Airtable automation will re-fire
 * when Send To Watch Folder is toggled.
 * 
 * @param {string} baseId - The club's Airtable base ID
 * @param {string} assetId - The asset record ID in the assets table
 * @param {string} venue - "MCG" or "Marvel" to determine which flag to clear
 */
export async function clearAssetIngestFlag(baseId, assetId, venue) {
  if (!assetId || !baseId) {
    console.warn('[OperatorAirtable] clearAssetIngestFlag: missing assetId or baseId');
    return;
  }
  
  const assetsTableId = process.env.REACT_APP_AIRTABLE_ASSETS_TABLE_ID;
  if (!assetsTableId) {
    console.warn('[OperatorAirtable] clearAssetIngestFlag: no REACT_APP_AIRTABLE_ASSETS_TABLE_ID');
    return;
  }
  
  // Determine which ingest flag to clear based on venue
  const v = (venue || '').toLowerCase();
  let fieldName = null;
  if (v.includes('mcg') || v.includes('melbourne cricket')) {
    fieldName = 'MCG Ingested';
  } else if (v.includes('marvel') || v.includes('docklands')) {
    fieldName = 'Marvel Ingested';
  }
  
  if (!fieldName) {
    console.warn(`[OperatorAirtable] clearAssetIngestFlag: could not determine venue from "${venue}"`);
    return;
  }
  
  console.log(`[OperatorAirtable] Clearing ${fieldName} on asset ${assetId} for force resend`);
  
  const url = tableUrl(baseId, assetsTableId);
  await airtableFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({
      records: [{ id: assetId, fields: { [fieldName]: false } }],
    }),
  });
  
  console.log(`[OperatorAirtable] ✅ ${fieldName} cleared on ${assetId}`);
}

// ================= EXPORTS =================

export {
  OPERATOR_BASES,
  OPERATOR_FIELDS,
  OPERATOR_CONTENT_TYPES,
};
